import styles from "./Payment.module.scss";
import { FC, useEffect, useRef, useState } from "react";
import Offcanvas from "@/components/offcanvas/Offcanvas";
import PaymentProgressBar from "@/components/payment-progress-bar/PaymentProgressBar";
import PaymentPlans from "@/components/payment-plans/PaymentPlans";
import PaymentStripe from "@/components/payment-stripe/PaymentStripe";
import PaymentWelcome from "@/components/payment-welcome/PaymentWelcome";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBadgeCheck, faPlus, faStar, faStarShooting } from "@fortawesome/pro-solid-svg-icons";
import { plansFeatures } from "@/lib/vars";
import { effectiveCheckoutParams } from "@/lib/checkoutIntent";
import useWelcomeDestination from "src/hooks/useWelcomeDestination";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { codeAppliedState, darkerBackgroundColorState, lighterBackgroundColorState, paymentModalOpenState, planState, subscriptionState, textColorState, themeState, userDataState } from "@/lib/store";
import { Box, Button, ButtonGroup, Dialog, Paper, Typography, useMediaQuery, CircularProgress, Skeleton } from "@mui/material";
import { classNames } from "@/lib/utils";
import { useRouter } from "next/router";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useOfferEligibility from "src/hooks/useOfferEligibility";
import { restartSubscription } from "@/controllers/subscription";

// Format a price for display: always 2 decimals when fractional, no decimals
// when whole. £34.3 → "34.30", £49 → "49", £24.5 → "24.50". Centralised so
// every price in the discount box renders consistently.
const formatPrice = (n: number | null | undefined): string => {
    if (n === null || n === undefined || !Number.isFinite(n)) return "";
    if (n % 1 === 0) return String(Math.round(n));
    return n.toFixed(2);
};

interface PropsTypes {
    noCloseButton?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    isAppSubscriber?: boolean;
    hidden?: boolean;
    onPaymentComplete?: () => void;
}

export const PaymentFlow: FC<PropsTypes> = ({ noCloseButton, setIsOpen, isAppSubscriber, onPaymentComplete }) => {
    const userData = useRecoilValue(userDataState);
    const theme = useRecoilValue(themeState);
    const lighterBackgroundColor = useRecoilValue(lighterBackgroundColorState);
    const darkerBackgroundColor = useRecoilValue(darkerBackgroundColorState);
    const textColor = useRecoilValue(textColorState);

    const [step, setStep] = useState(1);
    const welcomeDestination = useWelcomeDestination(step === 3);

    useEffect(() => {
        if (step === 3) onPaymentComplete?.();
    }, [step]);

    const [payment, setPayment] = useState({});
    const [disabled, setDisabled] = useState(false);
    const subscription = useRecoilValue(subscriptionState);
    const setSubscription = useSetRecoilState(subscriptionState);
    const [plan, setPlan] = useRecoilState(planState);
    const codeApplied = useRecoilValue(codeAppliedState);
    const setCodeApplied = useSetRecoilState(codeAppliedState);

    // NOTE (go-live 2026-07): first-signup Advanced pricing is FULL price —
    // the old ADV50/ADV30Y auto-apply is gone. ADV50 survives only as the
    // exit offer (the didn't-pay-right-away popup in the outer <Payment>),
    // which routes back through the standard ?promo= plumbing below.
    const [lastNameInvalid, setLastNameInvalid] = useState(false);
    const [firstNameInvalid, setFirstNameInvalid] = useState(false);

    const { query, isReady } = useRouter();

    // URL query wins; the persisted checkout intent fills the gap so a shared
    // link's interval/tier survive the signup / /upgrade hops that strip the
    // query string (see src/lib/checkoutIntent.ts).
    const resolveInterval = (q: typeof query): "annually" | "monthly" => {
        const t = effectiveCheckoutParams(q).t;
        return t === "monthly" || q.pt === "m" ? "monthly" : "annually";
    };

    const [interval, setInterval] = useState<"annually" | "monthly">("annually");

    useEffect(() => {
        if (isReady) setInterval(resolveInterval(query));
    }, [isReady]);

    const isPlanBasic = plan?.product_name.includes("Basic");
    const isDisabled = !subscription?.plans?.length || (step === 2 && (disabled || firstNameInvalid || lastNameInvalid));
    const [discountInfo, setDiscountInfo] = useState<any>();

    const autoCheckoutApplied = useRef(false);
    const initialPlanApplied = useRef(false);
    const urlPromoApplied = useRef(false);

    // Reset refs and step when subscription is cleared (modal re-opened fresh).
    // We ALSO reset codeApplied — otherwise a stale "ADV50"/"ADV30Y" survives
    // in Recoil across modal closes, and on the next open the default-offer
    // effect sees "codeApplied is already what I'd apply" and bails, leaving
    // the freshly-loaded subscription showing undiscounted prices. URL-driven
    // codes (FEMALEINVEST, promo-link landings) get re-set by their own
    // mount-time handlers, so resetting here is safe for those flows too.
    useEffect(() => {
        if (!subscription) {
            setStep(1);
            initialPlanApplied.current = false;
            autoCheckoutApplied.current = false;
            urlPromoApplied.current = false;
            setCodeApplied("");
        }
    }, [subscription]);

    // ── URL promo auto-apply (?promo=TRYAI etc.) ─────────────────────────
    // Belt-and-braces safety net. The PRIMARY trigger is
    // useOpenSubscriptionForm's promo read, which fires applyPromoCode at
    // modal-open time. That works in the happy path, but there are paths
    // where the subscription gets loaded without going through it (Stripe
    // 4xx that returns plans without invalid_promo_code, direct restart
    // calls from upstream code, query-hydration races on the first paint).
    // When that happens the user lands on a fully-loaded checkout with the
    // promo NOT applied — and has to hit "Apply" manually, which is
    // exactly the bug we're fixing.
    //
    // Effect: once subscription is loaded, if the URL carries ?promo= and
    // codeApplied doesn't already match, fire restartSubscription with
    // the URL code. Guarded by a one-shot ref so a re-render with the
    // same query doesn't loop. The controllers/subscription dedup map
    // ensures we don't double-fire alongside an in-flight openSub call —
    // both consumers share the same promise.
    useEffect(() => {
        if (!isReady) return;
        if (!subscription) return;
        if (isAppSubscriber) return;
        if (urlPromoApplied.current) return;

        const promoFromUrl = typeof query.promo === "string" && query.promo !== "true" ? query.promo : null;
        if (!promoFromUrl) return;

        // Already applied (by openSubscriptionForm or a manual Apply).
        // Mark the ref so we never re-fire for this modal-open.
        if (codeApplied && codeApplied.toUpperCase() === promoFromUrl.toUpperCase()) {
            urlPromoApplied.current = true;
            return;
        }

        // Skip if the user has a non-URL code already applied. FEMALEINVEST
        // (sign-up flow), APPDISC (app-subscriber path), and manually-typed
        // codes all live in codeApplied and should not be overwritten by a
        // stale URL promo. The matching-promo case above already handles
        // "same code already applied".
        if (codeApplied && !["", "ADV50", "ADV30Y"].includes(codeApplied.toUpperCase())) {
            urlPromoApplied.current = true;
            return;
        }

        urlPromoApplied.current = true;
        // Resolve interval from URL directly: ?t=monthly takes precedence
        // over Payment's `interval` state, which could still be the
        // default ("annually") if the URL-driven setInterval effect
        // hasn't committed when this effect runs.
        const targetInterval = resolveInterval(query);
        const targetTier = effectiveCheckoutParams(query).v === "basic" ? "Basic" : "Advanced";
        restartSubscription(promoFromUrl)
            .then((res) => {
                if (res?.payment_data?.invalid_promo_code) return; // bad code — leave state untouched
                setSubscription(res);
                const matchedPlan = res?.plans?.find(
                    (p) => p.product_name?.includes(targetTier) && p.plan_interval === targetInterval
                );
                if (matchedPlan) setPlan(matchedPlan);
                setCodeApplied(promoFromUrl);
            })
            .catch(() => {
                /* swallow — leave undiscounted price visible, user can hit Apply if they want */
            });
    }, [isReady, subscription, query.promo, query.t, query.v, codeApplied, isAppSubscriber]);

    useEffect(() => {
        if (!subscription) return;

        if (isAppSubscriber || codeApplied?.toUpperCase() === "FEMALEINVEST") {
            setStep(2);
            setPlan(subscription?.plans.find((plan) => plan.product_name.includes("Advanced") && plan.plan_interval === interval));
        } else {
            // Enforce plan from URL params (with the persisted checkout intent as
            // fallback — signup and the /upgrade hop strip the query string) once
            // both subscription and router are ready. Use resolveInterval(query)
            // directly to avoid stale closure when interval state and
            // subscription load in the same React flush.
            const eff = effectiveCheckoutParams(query);
            if (!initialPlanApplied.current && isReady && eff.v) {
                initialPlanApplied.current = true;
                const tier = eff.v === "basic" ? "Basic" : "Advanced";
                const urlInterval = resolveInterval(query);
                const urlPlan = subscription.plans.find((p) => p.product_name.includes(tier) && p.plan_interval === urlInterval);
                if (urlPlan) setPlan(urlPlan);
            }

            // Only skip the "Choose plan" step when the URL (or saved intent)
            // fully specifies the plan (?v=tier & ?t=interval) — e.g. a shared
            // teaser link that pre-picked Advanced monthly. `?checkout=true` on
            // its own just opens the modal (handled in _app); the user still
            // picks a plan on step 1, with any promo already applied.
            const hasFullPlanSelection = !!eff.v && !!eff.t;

            if (hasFullPlanSelection && !autoCheckoutApplied.current && isReady) {
                autoCheckoutApplied.current = true;
                setStep(2);
            } else if (!autoCheckoutApplied.current) {
                setStep(1);
            }
        }
    }, [isAppSubscriber, codeApplied, subscription, interval, isReady]);

    const handleIntervalChange = (newInterval: "annually" | "monthly") => {
        setInterval(newInterval);
        if (subscription?.plans) {
            setPlan(subscription?.plans.find((plan) => plan.product_name.includes("Advanced") && plan.plan_interval === newInterval));
        }
    };

    const onClick = () => {
        switch (step) {
            case 1:
                setStep(2);
                break;
            case 2:
                setPayment({});
                break;
            case 3:
                setIsOpen(false);
                window.location.href = `${window.location.origin}${welcomeDestination.path}`;
        }
    };

    useEffect(() => {
        const newDiscountInfo = subscription?.payment_data?.plans?.find((p) => p.id === plan?.plan_id);

        if (newDiscountInfo) {
            setDiscountInfo(newDiscountInfo);
        }
    }, [plan]);

    const getButtonText = () => {
        switch (step) {
            case 1:
                return "Checkout";
            case 2:
                return "Complete your payment";
            case 3:
                return "Begin your journey";
        }
    };

    const promoFromUrl = typeof query.promo === "string" && query.promo !== "true" ? query.promo : null;
    const waitingForPromo = promoFromUrl && !codeApplied && !subscription?.payment_data?.invalid_promo_code;

    // A discount block (special-flow or generic promo) renders on the summary panel
    // only when there's something to discount. When there isn't — no code, or a code
    // that doesn't discount the *selected* plan (e.g. Basic) — we fall back to the
    // plain price caption so a price is ALWAYS shown.
    const showsDiscountBlock =
        !!plan &&
        (!!userData?.app_subscriber_only ||
            codeApplied?.toUpperCase() === "FEMALEINVEST" ||
            (!!codeApplied && !!discountInfo?.original_price));

    return (
        <>
            <div className={styles.logo}>
                <Image unoptimized src="/assets/ortex-logo-h.png" alt="ortex-logo" width={150} height={30} priority />
            </div>

            <div className={classNames(styles.Payment, isAppSubscriber && styles.isAppSubscriber)}>
                <Box className={styles.container}>
                    {step === 1 && <PaymentPlans interval={interval} plan={plan} plans={subscription?.plans} setInterval={setInterval} setPlan={setPlan} setStep={setStep} onClose={setIsOpen} />}
                    {step === 2 && (
                        <>
                            {isAppSubscriber && (
                                <Box className={styles.appSuscriberText}>
                                    <Box>
                                        <Typography variant="h1" component="h1" className={styles.title}>
                                            {userData?.first_name ? `${userData.first_name}, welcome` : "Welcome"} to the web version of ORTEX
                                        </Typography>
                                        <Typography variant="body1" component="p" className={styles.subtitle}>
                                            To get full access to the web version you need to upgrade your account.
                                        </Typography>
                                        <Typography variant="body1" component="p" className={styles.subtitle}>
                                            As an app user you get a <b>50% off</b> your initial purchase.
                                        </Typography>
                                    </Box>
                                    <ButtonGroup size="small" sx={{ m: "0 auto", mt: 2 }}>
                                        <Button onClick={() => handleIntervalChange("annually")} variant={interval === "annually" ? "contained" : "outlined"} sx={{ textTransform: "initial", color: textColor, fontSize: "16px" }}>
                                            Yearly
                                        </Button>
                                        <Button onClick={() => handleIntervalChange("monthly")} variant={interval === "annually" ? "outlined" : "contained"} sx={{ textTransform: "initial", color: textColor, fontSize: "16px" }}>
                                            Monthly
                                        </Button>
                                    </ButtonGroup>
                                </Box>
                            )}
                            {!waitingForPromo && (
                                <PaymentStripe
                                    key={codeApplied}
                                    payment={payment}
                                    setStep={setStep}
                                    disabled={disabled}
                                    setDisabled={setDisabled}
                                    firstNameInvalid={firstNameInvalid}
                                    setFirstNameInvalid={setFirstNameInvalid}
                                    lastNameInvalid={lastNameInvalid}
                                    setLastNameInvalid={setLastNameInvalid}
                                />
                            )}
                        </>
                    )}
                    {step === 3 && <PaymentWelcome plan={plan} closeModal={setIsOpen} />}
                </Box>
                <Paper elevation={3} className={styles["right-container"]} sx={{ backgroundColor: theme === "dark" ? lighterBackgroundColor : darkerBackgroundColor }}>
                    <Box>
                        <Box>
                            <PaymentProgressBar number={step} />
                        </Box>
                        <Box className={styles.title}>
                            <FontAwesomeIcon icon={isPlanBasic ? faStar : faStarShooting} color="#31aba6" className={styles["star-icon"]} />
                            <Box sx={{ fontSize: "20px !important", flex: 1, minWidth: 0 }}>
                                <Typography variant="body1" component="p" sx={{ fontSize: "20px !important" }}>
                                    {plan ? plan?.product_name + " Plan" : <Skeleton variant="text" width={150} height={32} />}
                                </Typography>
                                {/*
                                    Discount display — three states:

                                    1. No discount applied: small "Billed
                                       annually at £X" caption beneath the
                                       plan name. Plain, low-key.

                                    2. Special-flow discount (APPDISC for
                                       app subscribers, FEMALEINVEST):
                                       hero-style "First X at £Y" with the
                                       full price after as the strikethrough
                                       reference. Anchors the user to the
                                       low introductory price.

                                    3. Generic promo discount applied: hero
                                       price in brand colour, original price
                                       struck through, prominent "SAVE X%"
                                       badge, and a small footnote for
                                       duration.

                                    Goal: the discounted price has to be the
                                    biggest thing on the panel. The previous
                                    layout buried the savings in a 11px line
                                    nobody read.
                                */}
                                {!showsDiscountBlock && (
                                    <Typography variant="body1" component="p" sx={{ fontSize: "11px", color: theme === "dark" ? "#939598" : "#3f3e40" }}>
                                        {plan ? (
                                            (() => {
                                                const isYearly = plan.plan_interval !== "monthly";
                                                const perMonth = isYearly ? plan.plan_price / 12 : plan.plan_price;
                                                return `${plan.currency_symbol}${formatPrice(perMonth)}/month${isYearly ? ", billed annually" : ""}`;
                                            })()
                                        ) : (
                                            <Skeleton variant="text" width={120} height={20} />
                                        )}
                                    </Typography>
                                )}
                                {codeApplied && (userData?.app_subscriber_only || codeApplied?.toUpperCase() === "FEMALEINVEST") && plan && (
                                    <Box
                                        sx={{
                                            mt: 1.25,
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            background: "linear-gradient(135deg, rgba(49,171,166,0.12) 0%, rgba(49,171,166,0.04) 100%)",
                                            border: "1px solid rgba(49,171,166,0.45)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                            <CheckCircleIcon sx={{ color: "#31aba6", fontSize: 18 }} />
                                            <Typography component="span" sx={{ fontSize: "12px", fontWeight: 700, color: "#31aba6", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                                {plan.plan_interval === "monthly" ? "First month" : "First year"}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                                            <Typography component="span" sx={{ fontSize: "34px", fontWeight: 800, lineHeight: 1, color: theme === "dark" ? "#fff" : "#1c1c1c" }}>
                                                {plan.currency_symbol}
                                                {formatPrice(plan.plan_price)}
                                            </Typography>
                                            {discountInfo?.original_price && discountInfo.original_price !== plan.plan_price && (
                                                <Typography component="span" sx={{ fontSize: "16px", color: theme === "dark" ? "#939598" : "#7a7a7a", textDecoration: "line-through" }}>
                                                    {plan.currency_symbol}
                                                    {formatPrice(discountInfo.original_price)}
                                                </Typography>
                                            )}
                                        </Box>
                                        {discountInfo?.original_price && (
                                            <Typography component="p" sx={{ mt: 0.5, fontSize: "11px", color: theme === "dark" ? "#939598" : "#3f3e40" }}>
                                                Then {plan.currency_symbol}
                                                {formatPrice(discountInfo.original_price)} per {plan.plan_interval === "monthly" ? "month" : "year"}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                {codeApplied && !(userData?.app_subscriber_only || codeApplied?.toUpperCase() === "FEMALEINVEST") && discountInfo?.original_price && plan && (
                                    <Box
                                        sx={{
                                            mt: 1.25,
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            background: "linear-gradient(135deg, rgba(49,171,166,0.14) 0%, rgba(49,171,166,0.04) 100%)",
                                            border: "1px solid rgba(49,171,166,0.5)",
                                            position: "relative",
                                        }}
                                    >
                                        {/* SAVE badge — top-right corner, strong contrast.
                                            Prefers the explicit % discount when the backend
                                            sent it, falls back to a computed % from prices. */}
                                        {(() => {
                                            const pct = discountInfo.discount_pc
                                                ? Math.round(discountInfo.discount_pc)
                                                : discountInfo.original_price
                                                ? Math.round(((discountInfo.original_price - plan.plan_price) / discountInfo.original_price) * 100)
                                                : 0;
                                            if (pct <= 0) return null;
                                            return (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: -10,
                                                        right: 12,
                                                        background: "#31aba6",
                                                        color: "#fff",
                                                        fontSize: "11px",
                                                        fontWeight: 800,
                                                        letterSpacing: "0.06em",
                                                        textTransform: "uppercase",
                                                        padding: "3px 9px",
                                                        borderRadius: 999,
                                                        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                                                    }}
                                                >
                                                    Save {pct}%
                                                </Box>
                                            );
                                        })()}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                                            <CheckCircleIcon sx={{ color: "#31aba6", fontSize: 18 }} />
                                            <Typography component="span" sx={{ fontSize: "12px", fontWeight: 700, color: "#31aba6", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                                Discount applied
                                            </Typography>
                                        </Box>
                                        {/* Headline price. For YEARLY plans we deliberately show
                                            the per-month equivalent as the big number (with the
                                            yearly total + renewal copy below) — "£34.30/month,
                                            charged £411.60 yearly" reads way better than
                                            "£411.60/yr"; the customer mentally compares to the
                                            monthly tier they were considering. For MONTHLY plans
                                            the per-month price IS the headline already. */}
                                        {(() => {
                                            const isYearly = plan.plan_interval !== "monthly";
                                            const perMonth = isYearly ? plan.plan_price / 12 : plan.plan_price;
                                            const perMonthOriginal = isYearly && discountInfo.original_price
                                                ? discountInfo.original_price / 12
                                                : discountInfo.original_price;
                                            return (
                                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                                                    <Typography component="span" sx={{ fontSize: "34px", fontWeight: 800, lineHeight: 1, color: theme === "dark" ? "#fff" : "#1c1c1c" }}>
                                                        {plan.currency_symbol}
                                                        {formatPrice(perMonth)}
                                                    </Typography>
                                                    <Typography component="span" sx={{ fontSize: "13px", color: theme === "dark" ? "#939598" : "#7a7a7a" }}>
                                                        /month
                                                    </Typography>
                                                    {perMonthOriginal && perMonthOriginal !== perMonth && (
                                                        <Typography component="span" sx={{ fontSize: "16px", color: theme === "dark" ? "#939598" : "#7a7a7a", textDecoration: "line-through", ml: 0.5 }}>
                                                            {plan.currency_symbol}
                                                            {formatPrice(perMonthOriginal)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })()}
                                        {/* Duration / renewal footnote.

                                            YEARLY: "Only £X/month, charged £Y yearly. Renews at £Z/yr"
                                              — the headline already shows the per-month figure; here
                                              we spell out the actual yearly invoice amount and the
                                              undiscounted renewal price (year 2 onward).

                                            MONTHLY (multi-month coupon): "First N months at this price,
                                              then £Z/month".

                                            MONTHLY (1-month coupon): "First month at this price,
                                              then £Z/month". */}
                                        {(() => {
                                            const isYearly = plan.plan_interval !== "monthly";
                                            // `forever` coupons (e.g. affiliate codes) discount every
                                            // billing cycle for the life of the subscription, so the
                                            // discounted price IS the ongoing rate — never show a
                                            // "then full price" renewal. Takes precedence over the
                                            // yearly / multi-month / one-month branches below.
                                            const isForever = discountInfo?.duration === "forever";
                                            const months = discountInfo?.duration_in_months || 0;
                                            const renewsAt = `${plan.currency_symbol}${formatPrice(discountInfo.original_price)}/${isYearly ? "yr" : "mo"}`;
                                            let text: string;
                                            if (isForever && isYearly) {
                                                // Headline shows the per-month figure; spell out the
                                                // actual annual amount charged. A forever coupon keeps
                                                // discounting every renewal, so no "then full price".
                                                text = `You'll be charged ${plan.currency_symbol}${formatPrice(plan.plan_price)}/year. This discounted rate is locked for as long as you stay subscribed.`;
                                            } else if (isForever) {
                                                text = `This discounted rate is locked for as long as you stay subscribed: it never goes up.`;
                                            } else if (isYearly) {
                                                // For the discounted year-1 total + the renewal price.
                                                text = `Charged ${plan.currency_symbol}${formatPrice(plan.plan_price)} for the first year. Renews at ${renewsAt}.`;
                                            } else if (months <= 1) {
                                                text = `First month at the discounted price, then ${renewsAt}.`;
                                            } else {
                                                text = `First ${months} months at this price, then ${renewsAt}.`;
                                            }
                                            return (
                                                <Typography component="p" sx={{ mt: 0.5, fontSize: "11px", color: theme === "dark" ? "#939598" : "#3f3e40" }}>
                                                    {text}
                                                </Typography>
                                            );
                                        })()}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {!isAppSubscriber && !isPlanBasic && (
                            <Box className={styles.advanced}>
                                <Typography variant="body1" component="p" sx={{ marginRight: "5px" }}>
                                    Everything in Basic
                                </Typography>
                                <FontAwesomeIcon icon={faPlus} color="#31aba6" height={20} />
                            </Box>
                        )}
                        {isAppSubscriber && (
                            <Box className={styles.advanced}>
                                <Typography variant="body1" component="p" sx={{ marginRight: "5px" }}>
                                    App Access
                                </Typography>
                                <FontAwesomeIcon icon={faPlus} color="#31aba6" height={20} />
                            </Box>
                        )}
                        {(plansFeatures[plan?.product_name] || plansFeatures["ORTEX Basics"]).map((feature) => (
                            <Box key={feature.text} className={styles.feature}>
                                <FontAwesomeIcon icon={faBadgeCheck} color="#31aba6" height={20} />
                                <Typography variant="body1" component="p" sx={{ marginLeft: "10px" }}>
                                    {feature.text}
                                </Typography>
                            </Box>
                        ))}
                        {step === 1 && isPlanBasic && (
                            <Box className={styles.upgrade}>
                                <Typography variant="body1" component="p">
                                    Don't miss out: Upgrade to <span onClick={() => setPlan(subscription?.plans.find((plan) => plan.product_name.includes("Advanced") && plan.plan_interval === interval))}>Advanced</span>
                                </Typography>
                            </Box>
                        )}
                        <hr />
                        {step !== 3 && userData?.subscription_plan === "ORTEX Basic" && (
                            <Box className={styles.invoice}>
                                <Typography variant="body1">Your first invoice will be reduced by the existing balance from your current subscription</Typography>
                            </Box>
                        )}
                        <Button className={styles.btn} variant="contained" sx={{ textTransform: "initial" }} disabled={isDisabled} onClick={onClick}>
                            {getButtonText()}
                        </Button>
                    </Box>
                </Paper>
            </div>
        </>
    );
};

// Per-currency TRYAI teaser: first month of Advanced at 7.99 (79 SEK). One
// Stripe coupon + promotion code per currency — multi-currency coupons don't
// work on our Stripe API version, so the claim URL must carry the code that
// matches the customer's billing currency (see scratchpad/tryai_reprice_799.py
// in the web repo for the provisioning script).
const TEASER_BY_CURRENCY: Record<string, { code: string; price: string; full: string }> = {
    usd: { code: "TRYAIUSD", price: "$7.99", full: "$149" },
    eur: { code: "TRYAIEUR", price: "€7.99", full: "€149" },
    gbp: { code: "TRYAIGBP", price: "£7.99", full: "£119" },
    cad: { code: "TRYAICAD", price: "CA$7.99", full: "CA$199" },
    sek: { code: "TRYAISEK", price: "79 kr", full: "1,490 kr" },
};

const Payment: FC<PropsTypes> = ({ noCloseButton, hidden }) => {
    const [paymentModalOpen, setPaymentModalOpen] = useRecoilState(paymentModalOpenState);
    const paymentCompleted = useRef(false);
    // Exit offer: signup is full price now, so users who close checkout
    // WITHOUT paying get the TRYAI teaser (first month at 7.99 / 79 SEK).
    // Shown on EVERY close-without-paying — the server-side eligibility
    // gate ("tryai", once per 6 months per user) owns enforcement, and
    // claiming routes through the standard ?promo= plumbing.
    const [exitOfferOpen, setExitOfferOpen] = useState(false);
    const { eligible: exitOfferEligible } = useOfferEligibility("tryai");
    const router = useRouter();

    // The subscription payload (loaded while checkout was open) tells us the
    // customer's billing currency. It can be cleared by the time the exit
    // dialog renders, so remember the last currency we saw. Falls back to
    // USD copy with a local-currency footnote when we never learned it.
    const subscription = useRecoilValue(subscriptionState);
    const lastCurrency = useRef<string>("");
    useEffect(() => {
        const ccy = subscription?.plans?.find((p) => p.plan_currency)?.plan_currency?.toLowerCase();
        if (ccy) lastCurrency.current = ccy;
    }, [subscription]);

    const teaser = TEASER_BY_CURRENCY[lastCurrency.current] || TEASER_BY_CURRENCY.usd;
    const currencyKnown = !!TEASER_BY_CURRENCY[lastCurrency.current];

    const claimExitOffer = () => {
        setExitOfferOpen(false);
        router
            .replace(
                { pathname: router.pathname, query: { ...router.query, promo: teaser.code, t: "monthly" } },
                undefined,
                { shallow: true },
            )
            .finally(() => setPaymentModalOpen(true));
    };

    return (
        <div>
            <Offcanvas
                isOpen={paymentModalOpen}
                onClose={() => {
                    if (paymentCompleted.current) {
                        setPaymentModalOpen(false);
                        // Route the user via /welcome/upsell so we can offer the
                        // API and Index Rebalance add-ons against the card they
                        // just used. The page hands off to ``next`` after they
                        // activate or skip — landing on markets-overview with
                        // the in-house welcome tour.
                        const next = `/markets-overview?tour=welcome`;
                        window.location.href = `${window.location.origin}/welcome/upsell?next=${encodeURIComponent(next)}`;
                    } else {
                        setPaymentModalOpen(false);
                        // Deliberately no "only once" guard: the offer re-appears on
                        // every close-without-paying. Server-side tryai eligibility
                        // (once per 6 months) is the only gate.
                        if (exitOfferEligible) {
                            setExitOfferOpen(true);
                        }
                    }
                }}
                noCloseButton={noCloseButton}
                hidden={hidden}
            >
                <PaymentFlow
                    noCloseButton={noCloseButton}
                    setIsOpen={setPaymentModalOpen}
                    onPaymentComplete={() => {
                        paymentCompleted.current = true;
                    }}
                />
            </Offcanvas>
            <Dialog open={exitOfferOpen} onClose={() => setExitOfferOpen(false)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                        Try ORTEX for a month: just {teaser.price}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1, opacity: 0.85 }}>
                        Get your first month of ORTEX Advanced for {teaser.price}. The discount is
                        applied automatically at checkout.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, fontSize: "11px", opacity: 0.6 }}>
                        {currencyKnown
                            ? "Cancel anytime."
                            : "Or the equivalent in your local currency (£/€/CA$/kr): applied automatically at checkout. Cancel anytime."}
                    </Typography>
                    <Button variant="contained" size="large" fullWidth sx={{ fontWeight: 700, mb: 1 }} onClick={claimExitOffer}>
                        Try for {teaser.price}
                    </Button>
                    <Button variant="text" fullWidth onClick={() => setExitOfferOpen(false)}>
                        No thanks
                    </Button>
                </Box>
            </Dialog>
        </div>
    );
};

export default Payment;
